// src/pages/user/UserKits.tsx
// Route: /user/kits
//
// The yearly hardware package. Every online learner is posted one box a
// year with everything their lessons need (Makey Makey, circuit parts,
// Arduino, science supplies…). Parents give a delivery address here, follow
// the package, and confirm when it arrives.

import { useEffect, useState } from "react";
import {
  DeliveryAddress,
  KIT_TRACK_LABELS,
  ParentShipment,
  SHIPMENT_STATUS_LABELS,
  ShipmentStatus,
  useConfirmKitReceivedMutation,
  useGetMyKitsQuery,
  useSaveKitAddressMutation,
} from "../../app/api/kitsApi";
import { errorText } from "../../app/api/learningApi";
import { NIGERIAN_STATES } from "../../components/DeliveryModePicker";
import { fmtDay } from "./UserLearn";

import { Truck } from "lucide-react";
const STEPS: ShipmentStatus[] = ["ready_to_pack", "packed", "shipped", "delivered"];

const STEP_TEXT: Record<ShipmentStatus, string> = {
  awaiting_address: "We need your delivery address before we can send it.",
  ready_to_pack: "Your address is in. We'll pack the box soon.",
  packed: "Packed and waiting for the courier.",
  shipped: "On its way to you.",
  delivered: "Delivered. Happy building!",
  cancelled: "This package was cancelled. Please contact us if that's unexpected.",
};

const EMPTY: DeliveryAddress = {
  recipientName: "",
  phone: "",
  line1: "",
  line2: "",
  landmark: "",
  city: "",
  state: "",
};

const input =
  "w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2.5 text-sm text-[#16213e] outline-none focus:border-[#1b2d5e]";

function AddressForm({
  initial,
  suggestedRecipient,
  onDone,
}: {
  initial: DeliveryAddress | null;
  suggestedRecipient: string | null;
  onDone: () => void;
}) {
  const [a, setA] = useState<DeliveryAddress>({
    ...EMPTY,
    ...(initial ?? {}),
    recipientName: initial?.recipientName ?? suggestedRecipient ?? "",
  });
  const [save, { isLoading }] = useSaveKitAddressMutation();
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof DeliveryAddress) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setA({ ...a, [k]: e.target.value });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await save(a).unwrap();
      onDone();
    } catch (err) {
      setError(errorText(err, "Could not save the address"));
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <label className="text-xs font-semibold text-[#3b4560]">
        Who should receive it?
        <input className={`${input} mt-1`} value={a.recipientName ?? ""} onChange={set("recipientName")} required />
      </label>
      <label className="text-xs font-semibold text-[#3b4560]">
        Phone number (for the courier)
        <input className={`${input} mt-1`} value={a.phone ?? ""} onChange={set("phone")} type="tel" required placeholder="0803 000 0000" />
      </label>
      <label className="text-xs font-semibold text-[#3b4560] sm:col-span-2">
        Street address
        <input className={`${input} mt-1`} value={a.line1 ?? ""} onChange={set("line1")} required placeholder="House number and street" />
      </label>
      <label className="text-xs font-semibold text-[#3b4560]">
        Estate, building or area <span className="font-normal text-[#8189a0]">(optional)</span>
        <input className={`${input} mt-1`} value={a.line2 ?? ""} onChange={set("line2")} />
      </label>
      <label className="text-xs font-semibold text-[#3b4560]">
        Nearest landmark <span className="font-normal text-[#8189a0]">(helps the courier)</span>
        <input className={`${input} mt-1`} value={a.landmark ?? ""} onChange={set("landmark")} />
      </label>
      <label className="text-xs font-semibold text-[#3b4560]">
        City or town
        <input className={`${input} mt-1`} value={a.city ?? ""} onChange={set("city")} required />
      </label>
      <label className="text-xs font-semibold text-[#3b4560]">
        State
        <select className={`${input} mt-1`} value={a.state ?? ""} onChange={set("state")} required>
          <option value="">Choose a state</option>
          {NIGERIAN_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      {error && <p className="text-sm font-semibold text-[#b94a3a] sm:col-span-2">{error}</p>}
      <div className="flex gap-2 sm:col-span-2">
        <button
          type="submit"
          disabled={isLoading}
          className="rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.28)] hover:bg-[#24397a] disabled:opacity-50"
        >
          {isLoading ? "Saving…" : "Save address"}
        </button>
        {initial && (
          <button type="button" onClick={onDone} className="rounded-2xl px-4 py-2.5 text-sm font-semibold text-[#8189a0] hover:bg-[#f1f3f8]">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function Progress({ status }: { status: ShipmentStatus }) {
  const at = STEPS.indexOf(status);
  return (
    <div className="mt-4 flex items-center gap-1.5">
      {STEPS.map((step, i) => (
        <div key={step} className="flex-1">
          <div className={`h-2 rounded-full ${at >= i ? "bg-[#6cc2a0]" : "bg-[#e8ecf5]"}`} />
          <p className={`mt-1 text-[11.5px] font-semibold ${at >= i ? "text-[#2a7a56]" : "text-[#a6adbf]"}`}>
            {SHIPMENT_STATUS_LABELS[step]}
          </p>
        </div>
      ))}
    </div>
  );
}

function PackageCard({ s }: { s: ParentShipment }) {
  const [confirm, { isLoading }] = useConfirmKitReceivedMutation();
  const [msg, setMsg] = useState<string | null>(null);

  return (
    <div className="rounded-[24px] border border-[#e5e8ef] bg-white p-5 shadow-[0_4px_16px_rgba(27,45,94,0.05)]">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-lg font-bold text-[#16213e]">{s.childName}'s kit</p>
          <p className="text-[12.5px] text-[#8189a0]">
            {s.kitTitle ?? KIT_TRACK_LABELS[s.track]} · {s.academicYear}
          </p>
        </div>
        <span className="rounded-full bg-[#ebeff7] px-3 py-1 text-xs font-bold text-[#4a44b0]">
          {SHIPMENT_STATUS_LABELS[s.status]}
        </span>
      </div>

      <p className="mt-3 text-sm text-[#3b4560]">{STEP_TEXT[s.status]}</p>
      {s.status !== "cancelled" && s.status !== "awaiting_address" && <Progress status={s.status} />}

      {(s.courier || s.trackingRef) && (
        <div className="mt-4 rounded-2xl bg-[#f9fafc] px-4 py-3 text-sm text-[#16213e]">
          <Truck className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{s.courier ?? "Courier"}
          {s.trackingRef && (
            <>
              {" "}· Tracking number <strong>{s.trackingRef}</strong>
            </>
          )}
          {s.shippedAt && <p className="text-[12.5px] text-[#8189a0]">Sent {fmtDay(s.shippedAt)}</p>}
        </div>
      )}

      <details className="mt-4">
        <summary className="cursor-pointer text-sm font-semibold text-[#24397a]">What's in the box ({s.items.length})</summary>
        <ul className="mt-2 space-y-1 text-sm text-[#3b4560]">
          {s.items.map((i) => (
            <li key={i.name}>
              {i.qty > 1 ? `${i.qty} × ` : ""}
              {i.name}
              {i.unit ? ` (${i.unit})` : ""}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[12.5px] text-[#8189a0]">
          The final list is confirmed when the box is packed. Please keep everything together; it's used in lessons all year.
        </p>
      </details>

      {s.status === "shipped" && !s.receivedByParent && (
        <div className="mt-4">
          <button
            disabled={isLoading}
            onClick={async () => {
              try {
                const r = await confirm(s.id).unwrap();
                setMsg(r.message);
              } catch (err) {
                setMsg(errorText(err, "Could not confirm"));
              }
            }}
            className="rounded-2xl bg-[#6cc2a0] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#5bb08f] disabled:opacity-50"
          >
            We've received it
          </button>
          <p className="mt-1 text-[12.5px] text-[#8189a0]">Something missing or broken? Send us a message from the Messages page.</p>
        </div>
      )}
      {msg && <p className="mt-2 text-sm font-semibold text-[#2a7a56]">{msg}</p>}
    </div>
  );
}

export default function UserKits() {
  const { data, isLoading, error } = useGetMyKitsQuery();
  const d = data?.data;
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (d && !d.addressComplete) setEditing(true);
  }, [d]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="h-32 animate-pulse rounded-[24px] bg-[#ebeff7]" />
        <div className="h-48 animate-pulse rounded-[24px] bg-[#ebeff7]" />
      </div>
    );
  }
  if (error || !d) {
    return <p className="text-sm text-[#b94a3a]">{errorText(error, "Could not load your kit")}</p>;
  }

  const a = d.address;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div>
        <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">My Kit</h1>
        <p className="mt-1 text-sm text-[#5f6780]">
          Each year we post every online learner a box with all the hardware their lessons need, so nothing has to be bought
          separately.
        </p>
      </div>

      <div className="rounded-[24px] border border-[#e5e8ef] bg-white p-5 shadow-[0_4px_16px_rgba(27,45,94,0.05)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-base font-bold text-[#16213e]">Delivery address</p>
            {!d.addressComplete && (
              <p className="mt-1 text-sm text-[#b26a1d]">Please add the address where we should send the kit.</p>
            )}
          </div>
          {d.addressComplete && !editing && (
            <button onClick={() => setEditing(true)} className="text-sm font-semibold text-[#24397a] hover:underline">
              Change
            </button>
          )}
        </div>
        <div className="mt-3">
          {editing ? (
            <AddressForm initial={d.addressComplete ? a : null} suggestedRecipient={d.suggestedRecipient} onDone={() => setEditing(false)} />
          ) : (
            a && (
              <p className="whitespace-pre-line text-sm leading-6 text-[#3b4560]">
                {[a.recipientName, a.phone, a.line1, a.line2, a.landmark ? `Landmark: ${a.landmark}` : null, [a.city, a.state].filter(Boolean).join(", ")]
                  .filter(Boolean)
                  .join("\n")}
              </p>
            )
          )}
        </div>
        {d.addressComplete && (
          <p className="mt-3 text-[12.5px] text-[#8189a0]">
            Changing the address updates any package that hasn't been packed yet. If a package is already packed, please message us.
          </p>
        )}
      </div>

      {d.shipments.length === 0 ? (
        <div className="rounded-[24px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] p-8 text-center text-sm text-[#5f6780]">
          A kit is prepared for each child once their online enrolment is paid. It will appear here.
        </div>
      ) : (
        d.shipments.map((s) => <PackageCard key={s.id} s={s} />)
      )}
    </div>
  );
}
